import type { IncomingHttpHeaders } from 'http';

import { error, ok, Result } from '../domains/Result';
import { BundleData, Upsell, IBundle } from '../domains/Product';

import {
  RetrieveBundlesResponse,
  TGetBundles,
} from '../api/types/adwriteApiTypes';
import {
  Currency,
  IUpsellsResponse,
  TGetUpsells,
} from '../api/types/upsellsApiTypes';

import { adWriteApi } from '../api/adWriteApi';
import { upsellsApi } from '../api/upsellsApi';
import { IUpsellsData, BUNDLE_NAMES, CURRENCIES } from '../types';
import { formatPrice } from '../helpers/formatting';
import { AdTypes } from '../components/AdForm/types';
import { formatError } from '../domains/Error';

interface IProductDataSource {
  getBundles: TGetBundles;
  getUpsells: TGetUpsells;
}

interface IProductRepository {
  getBundles: (
    sectionName: string,
    payload: {
      currency: string;
      price: string;
      adTypes: AdTypes[];
      adId?: number;
      photoCount?: number;
    },
    sellerCurrency: Currency,
    republish: boolean,
    headers: IncomingHttpHeaders,
  ) => Promise<Result<Error, BundleData>>;
  getUpsells: (
    adId: number,
    currency: Currency,
    section: number,
    photoCount: number,
    wanted: boolean,
    price: string,
    headers: IncomingHttpHeaders,
  ) => Promise<Result<Error, Upsell>>;
}

function ProductRepository(
  ProductDataSource: IProductDataSource,
): IProductRepository {
  const getBundles: IProductRepository['getBundles'] = async (
    sectionName,
    payload,
    sellerCurrency,
    republish,
    headers,
  ) => {
    try {
      const response = await ProductDataSource.getBundles(
        sectionName,
        payload,
        republish,
        headers,
      );
      const bundles = mapBundles(response.data, sellerCurrency);
      return ok(bundles);
    } catch (err) {
      return error(formatError(err, 'An error occurred while getting bundles'));
    }
  };

  const getUpsells: IProductRepository['getUpsells'] = async (
    adId: number,
    currency: Currency,
    section: number,
    photoCount: number,
    wanted: boolean,
    price: string,
    headers: IncomingHttpHeaders,
  ) => {
    try {
      const formattedPrice = parseInt(price.replace(/,/g, ''));
      const response = await ProductDataSource.getUpsells(
        adId,
        currency,
        section,
        photoCount,
        wanted,
        formattedPrice,
        headers,
      );
      const upsells = mapUpsells(response.data);
      return ok(upsells);
    } catch (err) {
      return error(formatError(err, 'An error occurred while getting upsells'));
    }
  };

  return {
    getBundles,
    getUpsells,
  };
}

const formattedPriceOrFree = (
  currency: Currency,
  price: number,
  sterlingPrice?: number,
) =>
  currency === CURRENCIES.EUR
    ? price > 0
      ? `€${formatPrice(price)}`
      : 'FREE'
    : sterlingPrice && sterlingPrice > 0
    ? `£${formatPrice(sterlingPrice)}`
    : 'FREE';

const bundleFormatter = (bundle: IBundle) => {
  switch (bundle.name) {
    case 'Free':
    case 'Basic':
      return { ...bundle, name: BUNDLE_NAMES.BASIC, viewsCount: 1 };
    case 'Standard':
      return {
        ...bundle,
        isRecommended: true,
      };
    case 'Autobump':
      return {
        ...bundle,
        name: BUNDLE_NAMES.BUMP,
        viewsCount: 2,
        isRecommended: true,
      };
    case 'Spotlight':
      return { ...bundle, viewsCount: 3 };
    default:
      return bundle;
  }
  // TODO: Format add-on price
};

const mapBundles = (
  response: RetrieveBundlesResponse,
  sellerCurrency: Currency,
) => {
  const updatedBundles = response.bundles.map((bundle) => {
    const formattedBundle = bundleFormatter(bundle);
    return {
      ...formattedBundle,
      displayPrice: formattedPriceOrFree(
        sellerCurrency,
        bundle.price,
        bundle.sterlingPrice,
      ),
    };
  });

  const maxPhotoCount = response.bundles.some(
    (bundle) => bundle.maxPhotoCount > 10,
  )
    ? 20
    : 10;

  const viewsCountArrayLength = response.bundles.some(
    (bundle) => bundle.viewsCount > 3,
  )
    ? 5
    : 3;

  return {
    bundles: updatedBundles,
    maxPhotoCount,
    viewsCountArrayLength,
  };
};

function formatUpsellTitle(header?: string) {
  switch (header) {
    case 'SUPERBOOSTER':
      return 'Super Booster';
    case 'SPOTLIGHT':
      return 'Spotlight';
    case 'BUMP':
      return 'Bump';
    default:
      return 'Spotlight';
  }
}

const mapUpsells = (upsells: IUpsellsResponse[]) => {
  const updatedUpsells: IUpsellsData[] = upsells.map((upsell) => {
    return {
      ...upsell,
      title: formatUpsellTitle(upsell.type),
      suggestedProductModalID: upsell?.suggestedProductModal?.id,
    };
  });
  return {
    boostInfoList: updatedUpsells,
    bumpEligible: upsells.some((item) => item.type === 'BUMP'),
    spotlightEligible: upsells.some(
      (item) => item.type === 'SPOTLIGHT' || item.type === 'SUPERBOOSTER',
    ),
  };
};

export { ProductRepository };

export const productRepository = ProductRepository({
  getBundles: adWriteApi.retrieveBundles,
  getUpsells: upsellsApi.getUpsells,
});
